// @ts-check

/**
 * @file Generates a new auth token and reveals the one-time secret to the user.
 */

import {useRouter} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {FlashNotifications} from "flash-notifications"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Button from "../../src/components/button"
import Card from "../../src/components/card"
import ScreenTitle from "../../src/components/screen-title"
import Text from "../../src/components/text"
import TextInput from "../../src/components/text-input"
import {UserAuthToken} from "../../src/frontend-models/user-auth-token"
import errorMessage from "../../src/shared/error-message"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} AuthTokenNewScreenProps */

/**
 * @typedef {object} AuthTokenNewScreenState
 * @property {boolean} generating Whether a token is being generated.
 * @property {string | null} token One-time plaintext token shown after generation.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 */

/**
 * Auth token creation screen — collects a label, generates the token, then
 * reveals the one-time secret. The plaintext is never retrievable again.
 * @augments {ShapeComponent<AuthTokenNewScreenProps, AuthTokenNewScreenState>}
 */
class AuthTokenNewScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useRouter>} */
  router

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /**
   * Latest text typed into the name field. The input is uncontrolled
   * (`defaultValue`-style) so fast WebDriver `sendKeys` cannot leave the value
   * half-applied; we read this at submit time instead of binding `value`.
   * @type {string}
   */
  enteredName = ""

  /** @type {AuthTokenNewScreenState} */
  state = {
    generating: false,
    token: null,
    status: null
  }

  /** Initializes router and palette. */
  setup() {
    useLocale()
    this.router = useRouter()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the create form or the one-time token reveal.
   * @returns {ReactNode} Auth token creation content.
   */
  render() {
    const {generating, status, token} = this.s
    const colors = this.tt.colors
    const themeName = this.tt.themeName

    return (
      <View
        style={styles.screen ||= {flex: 1}}
        testID="authTokenNewScreen"
      >
        <ScreenTitle>{_("Generate auth token")}</ScreenTitle>
        {token ?
          <Card
            style={styles.revealCard ||= {
              gap: 12,
              maxWidth: 560,
              width: "100%"
            }}
            testID="authTokenNewScreen/reveal"
          >
            <Text
              style={styles[`revealWarning-${themeName}`] ||= {
                color: colors.warningText,
                fontWeight: 600
              }}
              testID="authTokenNewScreen/reveal/warning"
            >
              {_("Copy your token now. For your security, it will not be shown again.")}
            </Text>
            <TextInput
              editable={false}
              style={styles[`revealToken-${themeName}`] ||= {
                backgroundColor: colors.surfaceSunken,
                fontFamily: "monospace"
              }}
              testID="authTokenNewScreen/reveal/token"
              value={token}
            />
            <View
              style={styles.revealActions ||= {
                flexDirection: "row",
                gap: 8
              }}
              testID="authTokenNewScreen/reveal/actions"
            >
              <Button
                iconName="copy"
                label={_("Copy token")}
                onPress={this.tt.onCopyPress}
                testID="authTokenNewScreen/reveal/copyButton"
                variant="secondary"
              />
              <Button
                iconName="check"
                label={_("Done")}
                onPress={this.tt.onDonePress}
                testID="authTokenNewScreen/reveal/doneButton"
              />
            </View>
          </Card>
          :
          <Card
            style={styles.formCard ||= {
              gap: 12,
              maxWidth: 520,
              width: "100%"
            }}
            testID="authTokenNewScreen/form"
          >
            <Text
              style={styles[`label-${themeName}`] ||= {
                color: colors.textMuted,
                fontWeight: 600
              }}
              testID="authTokenNewScreen/form/nameLabel"
            >
              {_("Token name")}
            </Text>
            <TextInput
              onChangeText={this.tt.onNameChangeText}
              onSubmitEditing={this.tt.onGeneratePress}
              style={styles.nameInput ||= {fontSize: 16}}
              testID="authTokenNewScreen/form/nameInput"
            />
            {status &&
              <Text
                style={styles[`status-${themeName}`] ||= {
                  color: colors.dangerText,
                  fontWeight: 600
                }}
                testID="authTokenNewScreen/form/status"
              >
                {status.text}
              </Text>
            }
            <Button
              disabled={generating}
              iconName="key"
              label={generating ? _("Generating...") : _("Generate token")}
              onPress={this.tt.onGeneratePress}
              testID="authTokenNewScreen/form/generateButton"
            />
          </Card>
        }
      </View>
    )
  }

  /**
   * Records the latest token name without binding the input's value.
   * @param {string} value Label text from the input.
   * @returns {void} Nothing.
   */
  onNameChangeText = (value) => {
    this.enteredName = value
    if (this.s.status) this.s.status = null
  }

  /**
   * Generates the auth token and reveals its one-time plaintext value.
   * @returns {Promise<void>} Resolves when generation handling completes.
   */
  onGeneratePress = async () => {
    if (this.s.generating) return
    const name = this.enteredName.trim()
    if (!name) {
      this.s.status = {kind: "error", text: _("A name is required")}
      return
    }
    this.s.generating = true
    this.s.status = null
    try {
      const response = await UserAuthToken.generate({name})
      const token = typeof response?.token === "string" ? response.token : null
      if (!token) {
        throw new Error("Generate response did not include a token")
      }
      this.s.token = token
    } catch (error) {
      this.s.status = {kind: "error", text: errorMessage(error)}
    } finally {
      this.s.generating = false
    }
  }

  /**
   * Copies the revealed token to the clipboard when supported, otherwise
   * tells the user to select and copy it manually.
   * @returns {Promise<void>} Resolves when copy handling completes.
   */
  onCopyPress = async () => {
    const {token} = this.s
    if (!token) return
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(token)
        FlashNotifications.success(_("Token copied to clipboard"))
        return
      } catch {
        // Clipboard write can be blocked by permissions; fall through to the
        // manual-copy hint rather than failing the action.
      }
    }
    FlashNotifications.success(_("Select the token above to copy it"))
  }

  /**
   * Returns to the token list after the user has saved their token.
   * @returns {void} Nothing.
   */
  onDonePress = () => {
    this.router.replace("/auth-tokens?created=1")
  }
}

/** @type {NamedExoticComponent<AuthTokenNewScreenProps>} */
const AuthTokenNewScreenComponent = memo(shapeComponent(AuthTokenNewScreen))

export default AuthTokenNewScreenComponent
